import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  AlertTitle,
} from "@mui/material";
import { Close as CloseIcon, Print as PrintIcon } from "@mui/icons-material";
import PageLayout from "../components/Common/PageLayout";
import PageBanner from "../components/Common/PageBanner";

import { get } from "lodash";
import { getProducts } from "@smfa/utils/src";
import { useReactToPrint } from "react-to-print";

/**
 * Member dashboard - directed here upon login
 * @returns {JSX.Element}
 * @component
 */
const MemberDashboard = () => {
  const auth = useSelector((state) => state.auth) || {};
  const user = useSelector((state) => state.user) || {};
  const membership = useMemo(
    // Memoize membership to prevent unnecessary useEffect runs
    () => user?.data?.membership || {},
    [user?.data?.membership],
  );
  const membershipPayment = useMemo(
    () => membership?.membership || null,
    [membership?.membership],
  );
  const membershipRenewal = useMemo(
    () => membership?.renewal || {},
    [membership?.renewal],
  );
  // eslint-disable-next-line no-unused-vars
  const [products, setProducts] = useState([]);
  const [allPurchases, setAllPurchases] = useState([]); // Combined state for products and membership
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [sessionInitialized, setSessionInitialized] = useState(false); // New state to track session initialization
  const userProfile = user?.data?.profile || {};
  const isAuthenticated = Boolean(auth?.isAuthenticated);
  const navigate = useNavigate();
  const dialogContentRef = useRef(); // Ref for printing dialog content

  // Track session initialization to ensure user data is fully loaded
  useEffect(() => {
    // Check if user data has been properly loaded from session
    const hasUserData = user?.data && Object.keys(user.data).length > 0;
    const hasProfile =
      user?.data?.profile && Object.keys(user.data.profile).length > 0;

    // Session is considered initialized if:
    // 1. User is authenticated AND has user data, OR
    // 2. User is not authenticated (no need to wait for data)
    if (!isAuthenticated || (isAuthenticated && hasUserData && hasProfile)) {
      setSessionInitialized(true);
    }
  }, [isAuthenticated, user?.data]);

  // Protect the dashboard from unauthenticated access
  useEffect(() => {
    const _fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch products
        let productsData = [];
        try {
          const res = await getProducts();
          productsData = get(res, "data.data.products", []);
          setProducts(productsData);
        } catch (e) {
          // Don't fail if products fetch fails, maybe user only has membership
          log.warn("[Dashboard] Could not fetch products: ", e);
        }

        // Combine and sort data
        const combined = [...productsData];
        if (membership?.membership) {
          // Adapt membership structure to match product structure for easier rendering
          combined.push({
            payment: membership.membership,
            product: membership.product,
            isMembership: true, // Add a flag to identify membership entries
          });
        }

        if (membership?.next) {
          combined.push({
            payment: membership.next,
            product: membership.next.product || membership.product,
            isMembership: true,
            isNextMembership: true,
          });
        }

        // Sort by payment date descending
        combined.sort((a, b) => {
          const dateA = a.payment?.payment_date
            ? new Date(a.payment.payment_date)
            : 0;
          const dateB = b.payment?.payment_date
            ? new Date(b.payment.payment_date)
            : 0;
          return dateB - dateA;
        });

        setAllPurchases(combined);
      } catch (e) {
        log.error("[Dashboard] Error fetching purchase data: ", e);
        setError("Failed to load purchase history. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (!isAuthenticated) {
      navigate("/login?redirect=/member-dashboard");
    } else if (sessionInitialized) {
      // Only fetch data after session is properly initialized
      _fetchData();
    }
  }, [membership, isAuthenticated, navigate, sessionInitialized]);

  // Dialog handlers
  const handleOpenDialog = (product) => {
    setSelectedProduct(product);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProduct(null);
  };

  // Print handler
  const handlePrint = useReactToPrint({
    contentRef: dialogContentRef,
    documentTitle: "Purchase Details",
    onPrintError: (error) => log.error("react-to-print error:", error),
  });

  // Format currency
  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) {
      return "N/A";
    }
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return dateString; // Return original if formatting fails
    }
  };

  // Format date (only date part)
  const formatDateOnly = (dateString) => {
    if (!dateString) return "N/A";
    try {
      // Add time component to ensure correct date parsing regardless of timezone
      const date = new Date(dateString.split(" ")[0] + "T12:00:00Z");
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: "UTC", // Specify UTC to avoid timezone shifts affecting the date
      });
    } catch (e) {
      return dateString; // Return original if formatting fails
    }
  };

  // Format date and time to YYYY-MM-DD h:mm:ss A TZD
  const formatApprovalDate = (dateString) => {
    if (!dateString) {
      return "N/A";
    }
    try {
      const date = new Date(dateString);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      let hours = date.getUTCHours();
      const minutes = String(date.getUTCMinutes()).padStart(2, "0");
      const seconds = String(date.getUTCSeconds()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      // const gHours = hours; // h for 12-hour format with leading zero, g for without. Using without.
      const displayHours = String(hours); // No leading zero for g as per PHP's g

      // Timezone is UTC as indicated by 'Z' in the input string
      return `${year}-${month}-${day} ${displayHours}:${minutes}:${seconds} ${ampm} UTC`;
    } catch (e) {
      log.error("Error formatting approval date:", e);
      return dateString; // Return original if formatting fails
    }
  };

  // Calculate expiration status for alerts
  const adminPlanType = user?.data?.profile?.plan_type;
  const isAdminUser = user?.data?.role === "admin";
  const membershipObject = user?.data?.membership;
  const hasPaidMembership = Boolean(
    membershipPayment &&
    !membershipPayment._is_expired &&
    membership?.product &&
    membership?.product?.price > 0,
  );
  const isComplimentaryMembership =
    membership?.payment_status === "complimentary" ||
    user?.data?.profile?.payment_status === "complimentary";
  const expirationStatus = useMemo(() => {
    // If session is not initialized yet, return loading state
    if (!sessionInitialized) {
      return { status: "loading" };
    }

    // If we have a profile but membership hasn't been hydrated yet, avoid
    // briefly showing "No Active Membership" while membership data loads.
    const membershipHydrated =
      Boolean(membershipPayment) ||
      Boolean(adminPlanType) ||
      (membershipObject && Object.keys(membershipObject).length > 0);
    if (!membershipHydrated) {
      return { status: "loading" };
    }

    // Only treat profile.plan_type as an admin-assigned membership when there
    // is no payment-based membership record. Some signup flows may populate
    // profile.plan_type even for normal memberships.
    const hasPaymentMembership = Boolean(membershipPayment?.plan_type);

    // Check for admin-assigned membership first (these don't expire)
    if (!hasPaymentMembership && adminPlanType && adminPlanType.length > 0) {
      return { status: "active", isAdminAssigned: true };
    }

    // Regular membership validation
    if (!membershipPayment) {
      return { status: "none" }; // No membership data
    } else if (membershipPayment._is_expired) {
      return {
        status: "expired",
        membershipYear: membershipPayment._membership_year,
        targetYear: membershipRenewal?.target_year,
      };
    }

    // Active membership -- check renewal window via server-provided data
    if (membershipRenewal?.should_warn) {
      return {
        status: "renewal_warning",
        membershipYear: membershipPayment._membership_year,
        targetYear: membershipRenewal.target_year,
        hasNextYear: membershipRenewal.has_next_year,
      };
    }

    // Active and next year already purchased
    if (membershipRenewal?.has_next_year) {
      return {
        status: "active",
        membershipYear: membershipPayment._membership_year,
        hasNextYear: true,
        nextYear: membershipRenewal.target_year,
      };
    }

    return {
      status: "active",
      membershipYear: membershipPayment._membership_year,
    }; // Active and not in renewal window
  }, [
    membershipPayment,
    membershipRenewal,
    adminPlanType,
    membershipObject,
    sessionInitialized,
  ]);

  const dashboardAlreadyIndicatesAdmin =
    isAdminUser &&
    expirationStatus?.status === "active" &&
    expirationStatus?.isAdminAssigned &&
    !hasPaidMembership &&
    isComplimentaryMembership;

  // Return null while redirect is happening
  if (!isAuthenticated) {
    return null;
  }

  log.debug("MemberDashboard: user: ", user);

  /**
   * Membership Status component - Displays what their current status is
   * @returns {JSX.Element}
   * @component
   */
  const MembershipStatus = () => {
    // Show loading state while session is initializing
    if (!sessionInitialized) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Loading Membership Status...</AlertTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              Checking your membership status...
            </Typography>
          </Box>
        </Alert>
      );
    }

    let title = "";
    let description = "";
    let buttonText = "Join"; // Set to {boolean}false to disable the button
    let buttonTo = "/membershipcategories";
    let alertSeverity = "info";
    let xJsx = null;

    switch (expirationStatus.status) {
      case "expired": {
        title = membershipPayment?.display?.status_title || "Membership Expired";
        description =
          membershipRenewal?.display?.expired_banner ||
          membershipPayment?.display?.status_body ||
          "Your membership has expired. Please renew to maintain access to member benefits.";
        buttonText = membershipRenewal?.checkout?.is_available
          ? "View Membership Plans"
          : false;
        alertSeverity = "error";
        break;
      }
      case "none":
        title = "No Active Membership";
        description =
          "You do not currently have an active membership. Sign up today to access member benefits.";
        alertSeverity = "error";
        break;
      case "renewal_warning": {
        title =
          membershipPayment?.display?.status_title ||
          "Membership Renewal Reminder";
        description =
          membershipRenewal?.display?.renewal_banner ||
          membershipPayment?.display?.status_body ||
          "Your membership is nearing renewal. Review the next term below.";
        buttonText = membershipRenewal?.checkout?.is_available
          ? "View Membership Plans"
          : false;
        alertSeverity = "warning";
        xJsx = (
          <Box
            sx={{
              mt: 1,
              pt: 1,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {membershipPayment?.term?.term_label ||
                membership?.product?.name ||
                "N/A"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {membershipPayment?.display?.coverage_label || "Coverage details unavailable."}
            </Typography>
            {membershipRenewal?.target_term?.term_label ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Next term: {membershipRenewal.target_term.term_label}
              </Typography>
            ) : null}
            {membershipPayment?.legacy_notice ? (
              <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                {membershipPayment.legacy_notice}
              </Typography>
            ) : null}
          </Box>
        );
        break;
      }
      case "active": {
        // Only show admin-assigned if there is no active paid membership and payment_status is 'complimentary'
        const hasPaidMembership = !!(
          membershipPayment &&
          !membershipPayment._is_expired &&
          membership?.product &&
          membership?.product?.price > 0
        );
        const isComplimentary =
          membership?.payment_status === "complimentary" ||
          user?.data?.profile?.payment_status === "complimentary";
        if (
          expirationStatus.isAdminAssigned &&
          !hasPaidMembership &&
          isComplimentary
        ) {
          title = isAdminUser
            ? "Membership Active (Admin Access)"
            : "Membership Active (Admin Assigned)";
          description = isAdminUser
            ? "Your membership access is administratively assigned. You may optionally purchase a paid membership plan at any time."
            : "Your membership has been administratively assigned. Enjoy your benefits!";
          buttonText = isAdminUser ? "Explore Membership Plans" : false;
          alertSeverity = "success";
          xJsx = (
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {user?.data?.profile?.plan_type || "Administrative Assignment"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This membership was assigned by an administrator and does not
                have an expiration date.
              </Typography>
              {isAdminUser ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Purchasing a paid membership is optional and does not affect
                  your existing admin permissions.
                </Typography>
              ) : null}
            </Box>
          );
        } else {
          title = "Membership Active";
          description =
            membershipPayment?.display?.status_body ||
            "Your membership is currently active. Enjoy your benefits!";
          buttonText = false;
          alertSeverity = "success";
          xJsx = (
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {membershipPayment?.term?.term_label ||
                  membership?.product?.name ||
                  "N/A"}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {membership?.product?.description || ""}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Coverage:</strong>{" "}
                {membershipPayment?.display?.coverage_label ||
                  formatDateOnly(membershipPayment?._expiration_date)}
              </Typography>
              {membership?.next?.term?.term_label ? (
                <Typography
                  variant="body2"
                  color="success.main"
                  sx={{ mt: 0.5, fontWeight: "medium" }}
                >
                  Paid for {membership.next.term.term_label}
                </Typography>
              ) : null}
              {membershipPayment?.legacy_notice ? (
                <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>
                  {membershipPayment.legacy_notice}
                </Typography>
              ) : null}
            </Box>
          );
        }
        break;
      }
      default:
        return null;
    }

    return (
      <Alert severity={alertSeverity} sx={{ mb: 2 }}>
        <AlertTitle>{title}</AlertTitle>
        {description}
        {buttonText && (
          <Button
            variant="outlined"
            component={Link}
            to={buttonTo}
            color="inherit"
            size="small"
            sx={{ ml: 1 }}
          >
            {buttonText}
          </Button>
        )}
        {xJsx}
      </Alert>
    );
  };

  // MemberDashboard JSX
  return (
    <PageLayout pageName="Member Dashboard" checkComingSoon={false}>
      <PageBanner dense mainTitle="Member Dashboard" pageTitle="Dashboard" />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={3}>
          {/* Welcome Card */}
          <Grid size={12}>
            <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
              <Typography variant="h5" gutterBottom>
                Welcome, {user.name || "Member"}!
              </Typography>

              <Typography variant="body1" sx={{ pb: 1 }}>
                This is your personal dashboard where you can access
                member-exclusive content, manage your profile, and view
                membership status.
              </Typography>
              {isAdminUser && !dashboardAlreadyIndicatesAdmin ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <AlertTitle>Administrator Account</AlertTitle>
                  You are signed in with administrator privileges.
                </Alert>
              ) : null}
              {/* Membership Status Component */}
              <MembershipStatus />
            </Paper>
          </Grid>

          {/* Member Resources */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div">
                  Member Resources
                </Typography>
                <Typography sx={{ mb: 1.5 }} color="text.secondary">
                  Access exclusive content
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate("/fiesta-sessions")}
                >
                  Fiesta Sessions
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate("/meeting")}
                >
                  Meetings (Discounted)
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* Profile Information */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div">
                  Your Profile
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1">
                    <strong>Name:</strong> {userProfile._displayName || ""}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Email:</strong> {user.data.email || ""}
                  </Typography>
                  {user?.data?.created_at && (
                    <Typography variant="body1">
                      <strong>Sign-up approval date:</strong>{" "}
                      {formatApprovalDate(user.data.created_at)}
                    </Typography>
                  )}
                </Box>
              </CardContent>
              <CardActions>
                <Button
                  variant="contained"
                  component={Link}
                  to="/profile"
                  size="small"
                >
                  Edit My Profile
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* Recent Purchases */}
          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div" gutterBottom>
                  Recent Purchases & Membership
                </Typography>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : error ? (
                  <Alert severity="error">{error}</Alert>
                ) : allPurchases.length === 0 ? (
                  <Typography variant="body1" color="text.secondary">
                    No purchases or active membership found.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} elevation={1}>
                    <Table
                      sx={{ minWidth: 650 }}
                      aria-label="recent purchases table"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Description</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allPurchases.map((item, index) => {
                          const payment = item.payment;
                          const product = item.product;
                          const rowStyle = product?.borderColor
                            ? {
                                borderLeft: `5px solid ${product.borderColor}`,
                                // Optionally use product.color
                                // color: product.color,
                              }
                            : {};

                          return (
                            <TableRow
                              key={payment?.id || `item-${index}`}
                              hover
                              onClick={() => handleOpenDialog(item)}
                              sx={{
                                ...rowStyle,
                                "&:hover": {
                                  cursor: "pointer",
                                  backgroundColor: "action.hover",
                                },
                              }}
                            >
                              <TableCell component="th" scope="row">
                                {product?.name ||
                                  (item.isMembership ? "Membership" : "N/A")}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(
                                  payment?.amount,
                                  payment?.currency,
                                )}
                              </TableCell>
                              <TableCell>
                                {
                                  formatDateTime(payment?.payment_date).split(
                                    ",",
                                  )[0]
                                }{" "}
                                {/* Show only date part in table */}
                              </TableCell>
                              <TableCell>
                                {product?.description || "N/A"}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Purchase Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Purchase Details
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedProduct && (
            <Box ref={dialogContentRef}>
              <Typography variant="h6" gutterBottom>
                {selectedProduct.product?.name ||
                  (selectedProduct.isMembership
                    ? "Membership Details"
                    : "Product Details")}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Description:</strong>{" "}
                {selectedProduct.product?.description || "N/A"}
              </Typography>
              <Typography variant="body1">
                <strong>Category:</strong>{" "}
                {selectedProduct.product?.category ||
                  (selectedProduct.isMembership ? "Membership" : "N/A")}
              </Typography>
              <Typography variant="body1">
                <strong>Amount Paid:</strong>{" "}
                {formatCurrency(
                  selectedProduct.payment?.amount,
                  selectedProduct.payment?.currency,
                )}
              </Typography>
              <Typography variant="body1">
                <strong>Payment Date:</strong>{" "}
                {formatDateTime(selectedProduct.payment?.payment_date)}
              </Typography>{" "}
              {/* Use formatDateTime */}
              <Typography variant="body1">
                <strong>Payment Status:</strong>{" "}
                {selectedProduct.payment?.status}
              </Typography>
              <Typography variant="body1">
                <strong>Payment Method:</strong>{" "}
                {selectedProduct.payment?.processor}
              </Typography>
              {selectedProduct.payment?.transaction_id && (
                <Typography variant="body2" color="text.secondary">
                  Transaction ID: {selectedProduct.payment.transaction_id}
                </Typography>
              )}
              <hr style={{ margin: "16px 0" }} />
              <Typography variant="subtitle1" gutterBottom>
                User Information
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {userProfile._displayName || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {user?.data?.email || "N/A"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Button onClick={handlePrint} startIcon={<PrintIcon />}>
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default MemberDashboard;
